import { changeBalanceVisibility } from "@shared/actions";
import {
  toggleContactsModal,
  toggleInfoModal,
  toggleSportBetsHistoryModal,
} from "@shared/actions/lobby";
import { useUi } from "@shared/hooks/interface";
import { useLobbyParams } from "@shared/hooks/lobby";
import { useFormatter, useOnClickOutside } from "@shared/hooks/shared";
import { useUser } from "@shared/hooks/user";
// import { useAllRules } from "@shared/modules/rules/api/get-all-rules";
import { Sweepstakes } from "@shared/modules/sweepstakes";
import cx from "classnames";
import { camelCase, get, startCase } from "lodash-es";
import React, { memo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";

import Modal from "../../Modal";

interface IUserPopup {
  onAuthExit: () => void;
  updateBalance: () => void;
}

export const UserPopup = memo(({ onAuthExit, updateBalance }: IUserPopup) => {
  const defaultTab = useRef(0);
  const dispatch = useDispatch();
  const intl = useIntl();
  const { isBalanceVisible } = useUi();

  const user = useUser() ?? {};
  const { sweepstakes: isSweepstakesEnabled, lobbyButtons } =
    useLobbyParams() ?? {};

  const [isPopupShown, setIsPopupShown] = useState(false);

  const [isModalShown, setIsModalShown] = useState(false);
  const dropdownClassName = cx({ dropdown_opened: isPopupShown });

  const $f = useFormatter();
  const balance = $f.formatMoney($f.toMoney(get(user, "balance", "")));
  const currency = get(user, "currency", "");
  const userName = user?.name || user?.displayable_name;

  const popupUserRef = useRef(null);
  const hasDeposit = lobbyButtons?.deposit?.enabled ?? false;
  const hasWithdraw = lobbyButtons?.withdraw.enabled ?? false;
  const isSignOutButtonShown = lobbyButtons?.exit.enabled ?? false;
  const isContactsButtonShown = lobbyButtons?.contacts?.enabled ?? false;
  // const { isSweepstakesRuleExist, isTermsRuleExist } = useAllRules();

  const getBalance = () => {
    if (!isSweepstakesEnabled) {
      return (
        <div className="user__category">
          {intl.formatMessage({
            id: "boilerplate.components.Popup.Balance",
          })}
          <div className="user__balance user__balance_primary">
            {currency} {balance}
          </div>
        </div>
      );
    }

    return (
      <div className="user__sweepstakes-wrapper">
        {" "}
        <Sweepstakes isSmall={true} />
      </div>
    );
  };

  const toggleSportBetsHistoryModalShow = () => {
    togglePopup();
    dispatch(toggleSportBetsHistoryModal(true));
  };

  const toggleInfoModalShow = () => {
    togglePopup();
    dispatch(toggleInfoModal(true));
  };
  // const toggleRulesModalShow = (
  //   type: "TermsAndConditions" | "SweepstakesRules"
  // ) => {
  //   togglePopup();
  //   dispatch(toggleRulesModal({ isShow: true, type }));
  // };

  const toggleModalContactShow = () => {
    togglePopup();
    dispatch(toggleContactsModal(true));
  };

  const openModal = (event: React.SyntheticEvent<EventTarget>) => {
    if (!(event.target instanceof HTMLLIElement)) {
      return;
    }

    const tab: string = event.target?.dataset.tabs ?? "0";

    defaultTab.current = parseInt(tab);
    setIsModalShown(true);
  };

  const closeModal = () => {
    defaultTab.current = 0;
    setIsModalShown(false);
    setIsPopupShown(false);
  };

  const toggleBalanceVisibility = () => {
    dispatch(changeBalanceVisibility(!isBalanceVisible));
    setIsPopupShown(false);
  };

  const togglePopup = () => setIsPopupShown(!isPopupShown);

  useOnClickOutside(popupUserRef, () => setIsPopupShown(false));

  return (
    <div ref={popupUserRef} className={`user dropdown ${dropdownClassName}`}>
      <div className="dropdown__opener" onClick={togglePopup}>
        <b className="user__name">{userName}</b>

        <i
          className="ico ico-b-arrow-down"
          role="presentation"
          aria-label="open/close menu"
        />

        {isBalanceVisible && !isSweepstakesEnabled && (
          <div className="user__balance user__balance_secondary">
            {currency} {balance}
          </div>
        )}
      </div>

      <div className="dropdown__drop">
        <div>
          <div className="dropdown__section">
            <ul className="list">
              <li className="list__item no-hover">{getBalance()}</li>
            </ul>
          </div>

          <div className="dropdown__section">
            <ul className="list list--flex">
              <li className="list__item">
                <span
                  className="user__link"
                  role="button"
                  onClick={updateBalance}
                >
                  <i className="ico ico-refresh" role="presentation" />

                  <span>
                    {intl.formatMessage({
                      id: "boilerplate.components.Popup.Refresh",
                    })}
                  </span>
                </span>
              </li>
              {!isSweepstakesEnabled && (
                <li className="list__item">
                  <span
                    className="user__link"
                    role="button"
                    onClick={toggleBalanceVisibility}
                  >
                    <i
                      className={cx(
                        "ico",
                        isBalanceVisible ? "ico-eye-slash" : "ico-eye"
                      )}
                      role="presentation"
                    />

                    <span>
                      {isBalanceVisible ? (
                        <>
                          {intl.formatMessage({
                            id: "boilerplate.components.Popup.HideBalance",
                          })}
                        </>
                      ) : (
                        <>
                          {intl.formatMessage({
                            id: "boilerplate.components.Popup.ShowBalance",
                          })}
                        </>
                      )}
                    </span>
                  </span>
                </li>
              )}
            </ul>
          </div>

          <div className="dropdown__section">
            <ul className="list">
              {(hasDeposit || hasWithdraw) && (
                <li
                  className="list__item list__btn"
                  data-tabs="0"
                  role="button"
                  onClick={openModal}
                >
                  {intl.formatMessage({
                    id: "boilerplate.components.Popup.Score",
                  })}
                </li>
              )}

              <li
                className="list__item list__btn"
                data-tabs={`${hasDeposit || hasWithdraw ? "1" : "0"}`}
                role="button"
                onClick={openModal}
              >
                {intl.formatMessage({
                  id: "boilerplate.components.Popup.Settings",
                })}
              </li>
              <li
                onClick={toggleSportBetsHistoryModalShow}
                role="button"
                className="list__item list__btn"
              >
                {intl.formatMessage({
                  id: "boilerplate.components.SportBetsHistoryModal.SportBetsHistory",
                  defaultMessage: "Sport Bets History",
                })}
              </li>
              <li
                onClick={toggleInfoModalShow}
                role="button"
                className="list__item list__btn"
              >
                {intl.formatMessage({
                  id: "boilerplate.info",
                })}
              </li>
              {isContactsButtonShown && (
                <li
                  onClick={toggleModalContactShow}
                  role="button"
                  className="list__item list__btn"
                >
                  {startCase(
                    camelCase(
                      intl.formatMessage({
                        id: "boilerplate.components.ModalContacts.title",
                      })
                    )
                  )}
                </li>
              )}
              {/*{isTermsRuleExist && (*/}
              {/*  <li*/}
              {/*    onClick={() => toggleRulesModalShow("TermsAndConditions")}*/}
              {/*    role="button"*/}
              {/*    className="list__item list__btn"*/}
              {/*  >*/}
              {/*    {intl.formatMessage({ id: "terms-and-conditions" })}*/}
              {/*  </li>*/}
              {/*)}*/}
              {/*{isSweepstakesEnabled && isSweepstakesRuleExist && (*/}
              {/*  <li*/}
              {/*    onClick={() => toggleRulesModalShow("SweepstakesRules")}*/}
              {/*    role="button"*/}
              {/*    className="list__item list__btn"*/}
              {/*  >*/}
              {/*    Sweepstakes rules*/}
              {/*  </li>*/}
              {/*)}*/}
              {isSignOutButtonShown && (
                <li
                  className="list__item list__btn"
                  role="button"
                  onClick={onAuthExit}
                >
                  {intl.formatMessage({
                    id: "sign-out",
                  })}
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      <Modal
        showModal={isModalShown}
        defaultTab={defaultTab}
        close={closeModal}
      />
    </div>
  );
});

UserPopup.displayName = "UserPopup";
